import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import type {
  BulkActionProgressItem,
  BulkActionProgress,
  FilterBasedBulkActionParams,
} from "@/lib/hooks/api/shared/bulk-filter-types";

export type { BulkActionProgressItem, BulkActionProgress, FilterBasedBulkActionParams };

export interface CompanyFilterCriteria {
  nameSearch?: string;
  status?: string;
  exclude_id?: string;
}

// Helper function to build the same query as useCompaniesPaginated
function buildCompanyFilterQuery(supabase: any, filterCriteria: CompanyFilterCriteria) {
  let query = supabase.from("companies").select("*");

  // Apply filters
  if (filterCriteria.status) {
    query = query.eq("status", filterCriteria.status);
  }
  if (filterCriteria.exclude_id) {
    query = query.neq("id", filterCriteria.exclude_id);
  }

  // Handle name search
  if (filterCriteria.nameSearch) {
    query = query.ilike("name", `%${filterCriteria.nameSearch}%`);
  }

  return query.order("created_at", { ascending: false });
}

// Get all company IDs matching the filter criteria (with pagination)
async function getFilteredCompanyIds(
  filterCriteria: CompanyFilterCriteria
): Promise<Array<{ id: string; name: string }>> {
  const supabase = getSupabase();
  const pageSize = 1000; // Supabase limit per query
  let allCompanies: Array<{ id: string; name: string }> = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const query = buildCompanyFilterQuery(supabase, filterCriteria);
    const { data, error, count } = await query
      .select("id, name", { count: "exact" })
      .range(from, to);

    if (error) {
      console.error("Error fetching filtered company IDs:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allCompanies = allCompanies.concat(data);
      // Check if we've fetched all records
      if (count !== null && allCompanies.length >= count) {
        hasMore = false;
      } else if (data.length < pageSize) {
        hasMore = false;
      }
      page++;
    }
  }

  return allCompanies;
}

// Get all contacts for a company (with pagination)
async function getCompanyContacts(
  companyId: string
): Promise<Array<{ id: string; email?: string }>> {
  const supabase = getSupabase();
  const pageSize = 1000; // Supabase limit per query
  let allContacts: Array<{ id: string; email?: string }> = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("contacts")
      .select("id, email", { count: "exact" })
      .eq("company_id", companyId)
      .range(from, to);

    if (error) {
      console.error("Error fetching company contacts:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allContacts = allContacts.concat(data);
      // Check if we've fetched all records
      if (count !== null && allContacts.length >= count) {
        hasMore = false;
      } else if (data.length < pageSize) {
        hasMore = false;
      }
      page++;
    }
  }

  return allContacts;
}

// Bulk export company contacts' emails by filter
export function useBulkExportCompaniesContactsEmailsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: CompanyFilterCriteria }) => {
      const companies = await getFilteredCompanyIds(filterCriteria);

      if (companies.length === 0) {
        throw new Error("No companies found matching the current filters");
      }

      // Fetch all contacts for each company and collect emails
      const allEmails = new Set<string>();

      for (const company of companies) {
        const contacts = await getCompanyContacts(company.id);
        contacts.forEach((contact) => {
          if (contact.email) {
            allEmails.add(contact.email);
          }
        });
      }

      if (allEmails.size === 0) {
        throw new Error("No contacts with email addresses found for the selected companies");
      }

      // Create semicolon-separated email list
      const emailList = Array.from(allEmails).join("; ");

      return {
        emails: Array.from(allEmails),
        emailList,
        totalCount: allEmails.size,
      };
    },
    onSuccess: (result) => {
      toast.success(`Found ${result.totalCount} unique email addresses from company contacts`);
    },
    onError: (error) => {
      toast.error("Failed to export company contact emails");
      console.error("Error exporting company contact emails by filter:", error);
    },
  });
}
